/* ============================================================================
   kitsters-analytics.js - GA4 dataLayer event hooks (theme-side)
   ----------------------------------------------------------------------------
   Pushes Google Analytics 4 "ecommerce" events to window.dataLayer so GA4 /
   GTM / Meta can measure the funnel. The OWNER still has to create the GA4
   property and connect it (GTM container or Shopify Customer Events / Web
   Pixel) - see docs/19. This file only emits the events; it does not load any
   analytics vendor.

   Events emitted (GA4 ecommerce spec):
     - view_item        on a product page (from the JSON config block)
     - add_to_cart      by observing fetch() calls to /cart/add (catches PDP,
                        collection cards, and the cart drawer uniformly)
     - view_cart        on the cart page (from JSON config) and when the cart
                        drawer opens (MutationObserver on #CartDrawer)
     - remove_from_cart by observing fetch() calls to /cart/change.js and
                        diffing the cart state before vs after
     - begin_checkout   on any click toward /checkout (reads /cart.js for items)
     - search           on the search results page (from the JSON config block)

   Note: "purchase" is NOT emitted here. On this store checkout is Shopify's
   own — there is no checkout-replacement app (the India store runs Razorpay
   Magic Checkout; that is India-only). The theme cannot see the thank-you
   page, so purchase must come from Shopify's Customer Events / Web Pixel or a
   GTM server container. Owner task; it is not a gap this file can close.

   Design notes:
     - Theme-agnostic: we observe the standard Shopify /cart/* endpoints rather
       than coupling to the theme's own ATC JS, so every path is captured.
     - Defensive: every handler is wrapped so a tracking error can never break
       the store (analytics must never block a sale).
   ========================================================================== */
(function () {
  'use strict';

  window.dataLayer = window.dataLayer || [];

  // ---- config (emitted by snippets/kitsters-analytics.liquid) --------------
  var cfg = {};
  try {
    var el = document.getElementById('kitsters-analytics-data');
    if (el) { cfg = JSON.parse(el.textContent || '{}'); }
  } catch (e) { cfg = {}; }
  /* ⚠️ NO HARDCODED FALLBACK CURRENCY. This read `|| 'INR'` on the India theme,
     which on a US store would have reported every dollar of revenue to GA4 as
     rupees — silently, in a system nobody reads back against the storefront,
     and with a ~85x error in the only number the business acts on.

     snippets/kitsters-analytics.liquid always supplies this from
     `cart.currency.iso_code | default: shop.currency`, so an empty value means
     the config block is missing, i.e. a real misconfiguration. In that case the
     events go out with NO currency key: GA4 then declines to attribute revenue,
     which is visible in the reports, where a wrong currency is not. Missing
     data you can spot beats wrong data you cannot (§10.66c). */
  var CURRENCY = cfg.currency || '';
  if (!CURRENCY && window.console && console.warn) {
    console.warn('[kitsters-analytics] no currency in the config block — ' +
                 'revenue events will omit `currency` rather than guess one.');
  }

  // ---- helpers -------------------------------------------------------------
  function push(eventName, ecommerce) {
    try {
      // Clear the previous ecommerce object first (GA4/GTM best practice so
      // values don't bleed between events).
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({ event: eventName, ecommerce: ecommerce });
    } catch (e) { /* never throw from analytics */ }
  }

  // Map a Shopify line item / cart-add response item to a GA4 item.
  // Shopify prices are in the shop's minor unit (paise) → divide by 100.
  function toGa4Item(li) {
    return {
      item_id: String(li.product_id || li.id || ''),
      item_name: li.product_title || li.title || '',
      item_variant: li.variant_title || '',
      item_brand: li.item_brand || 'Kitsters',
      price: Math.round(li.price || 0) / 100,
      quantity: li.quantity || 1
    };
  }

  function itemsValue(items) {
    return items.reduce(function (sum, i) { return sum + (i.price * i.quantity); }, 0);
  }

  // ---- Cart state cache (needed for remove_from_cart diffing) --------------
  // Keyed by variant_id (string) → { item: rawLineItem, qty: number }
  var cartSnapshot = {};

  function snapshotCart(cart) {
    var s = {};
    (cart.items || []).forEach(function (li) {
      s[String(li.variant_id)] = { item: li, qty: li.quantity };
    });
    cartSnapshot = s;
  }

  // Always calls the real browser fetch, not our /cart/* wrapper.
  // Defined early so the cart snapshot init below can use it.
  var nativeFetch = (typeof window.fetch === 'function') ? window.fetch : null;

  function nativeOrWindowFetch(u) {
    var fn = nativeFetch || window.fetch;
    if (!fn) { return Promise.reject(new Error('fetch unavailable')); }
    return fn.call(window, u, { headers: { 'Accept': 'application/json' } });
  }

  // Seed the snapshot asynchronously so the first remove_from_cart has data.
  try {
    nativeOrWindowFetch('/cart.js').then(function (r) { return r.json(); }).then(snapshotCart)['catch'](function () {});
  } catch (e) {}

  // ---- view_item (product pages) -------------------------------------------
  if (cfg.product) {
    push('view_item', {
      currency: CURRENCY,
      value: cfg.product.price,
      items: [cfg.product]
    });
  }

  // ---- search --------------------------------------------------------------
  // GA4 "search" event - uses search_term at the top level (not ecommerce).
  if (cfg.search && cfg.search.term) {
    try {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'search',
        search_term: cfg.search.term,
        search_results: cfg.search.results || 0
      });
    } catch (e) { /* noop */ }
  }

  // ---- view_cart (cart page) -----------------------------------------------
  if (cfg.cart && cfg.cart.items && cfg.cart.items.length) {
    push('view_cart', {
      currency: cfg.cart.currency || CURRENCY,
      value: cfg.cart.total,
      items: cfg.cart.items.map(toGa4Item)
    });
  }

  // ---- view_cart (cart drawer opens) ----------------------------------------
  // Observes #CartDrawer (both Dawn-style <details> and dialog approaches use
  // the "open" attribute; Kalles uses class changes too).
  (function () {
    function fireViewCartFromApi() {
      try {
        nativeOrWindowFetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (!cart.items || !cart.items.length) { return; }
            snapshotCart(cart);
            push('view_cart', {
              currency: cart.currency || CURRENCY,
              value: Math.round(cart.total_price || 0) / 100,
              items: cart.items.map(toGa4Item)
            });
          })['catch'](function () {});
      } catch (e) {}
    }

    function observeDrawer(selector, openTest) {
      try {
        var el = document.querySelector(selector);
        if (!el) { return; }
        var wasOpen = openTest(el);
        new MutationObserver(function () {
          var isOpen = openTest(el);
          if (isOpen && !wasOpen) { fireViewCartFromApi(); }
          wasOpen = isOpen;
        }).observe(el, { attributes: true, attributeFilter: ['open', 'class', 'aria-hidden', 'aria-expanded'] });
      } catch (e) {}
    }

    // Dawn / Kalles: #CartDrawer uses the native "open" attribute (details/dialog).
    observeDrawer('#CartDrawer', function (el) { return el.hasAttribute('open'); });
    // Dawn signals an open drawer with `active` on the <cart-drawer> element
    // rather than an attribute on #CartDrawer, so watch both states. The
    // India build had a second observer here for the Kalles element; Dawn's
    // own #CartDrawer is already covered by the line above.
    observeDrawer('cart-drawer', function (el) {
      return el.classList.contains('active') || el.hasAttribute('open');
    });
  }());

  // ---- fetch() interceptor (add_to_cart + remove_from_cart) ---------------
  // We intercept /cart/add.js  → add_to_cart
  //              /cart/change.js → remove_from_cart (when quantity drops)
  if (nativeFetch) {
    window.fetch = function () {
      var input = arguments[0];
      var url = '';
      try { url = (input && input.url) ? input.url : String(input || ''); } catch (e) { url = ''; }
      var promise = nativeFetch.apply(this, arguments);

      // ---- add_to_cart -----
      if (url && url.indexOf('/cart/add') !== -1) {
        try {
          promise.then(function (res) {
            if (!res || !res.clone) { return; }
            res.clone().json().then(function (data) {
              // /cart/add.js returns a single line item, or { items: [...] }
              var raw = (data && data.items) ? data.items : [data];
              var items = raw.filter(Boolean).map(toGa4Item);
              if (!items.length) { return; }
              push('add_to_cart', {
                currency: CURRENCY,
                value: itemsValue(items),
                items: items
              });
            })['catch'](function () {});
          })['catch'](function () {});
        } catch (e) {}
      }

      // ---- remove_from_cart -----
      // /cart/change.js response is the full updated cart. We diff against
      // cartSnapshot to find items whose quantity dropped (or hit 0).
      if (url && url.indexOf('/cart/change') !== -1) {
        try {
          var snapBefore = cartSnapshot; // capture before response resolves
          promise.then(function (res) {
            if (!res || !res.clone) { return; }
            res.clone().json().then(function (newCart) {
              var newItems = newCart.items || [];
              var newByKey = {};
              newItems.forEach(function (li) { newByKey[String(li.variant_id)] = li; });

              Object.keys(snapBefore).forEach(function (key) {
                var oldEntry = snapBefore[key];
                var newLi = newByKey[key];
                var newQty = newLi ? newLi.quantity : 0;
                var removedQty = oldEntry.qty - newQty;
                if (removedQty <= 0) { return; }

                // Build the GA4 item from the old snapshot (has full data)
                var ga4item = toGa4Item(oldEntry.item);
                ga4item.quantity = removedQty;
                push('remove_from_cart', {
                  currency: newCart.currency || CURRENCY,
                  value: Math.round((oldEntry.item.price || 0) * removedQty) / 100,
                  items: [ga4item]
                });
              });

              snapshotCart(newCart);
            })['catch'](function () {});
          })['catch'](function () {});
        } catch (e) {}
      }

      return promise;
    };
  }

  // ---- begin_checkout (clicks toward /checkout) ----------------------------
  // Fires on any checkout affordance: name="checkout", a[href*="/checkout"],
  // or [data-checkout]. We read /cart.js for the real basket; navigation is
  // not blocked (fire-and-forget).
  document.addEventListener('click', function (e) {
    var trigger;
    try {
      trigger = e.target && e.target.closest
        ? e.target.closest('[name="checkout"], a[href*="/checkout"], [data-checkout]')
        : null;
    } catch (err) { trigger = null; }
    if (!trigger) { return; }

    try {
      nativeOrWindowFetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        var items = (cart.items || []).map(toGa4Item);
        push('begin_checkout', {
          currency: (cart.currency || CURRENCY),
          value: Math.round(cart.total_price || 0) / 100,
          items: items
        });
      })['catch'](function () {});
    } catch (err) { /* noop */ }
  }, true);
}());
